<template>
  <div class="ui-pager">
    <UiButton variant="secondary" type="button" :disabled="page <= 1" @click="emit('prev')">
      Назад
    </UiButton>
    <nav v-if="showPages" class="ui-pager__pages" aria-label="Страницы">
      <template v-for="(page_item, index) in pageItems" :key="`${page_item}-${index}`">
        <span v-if="page_item === 'ellipsis'" class="ui-pager__ellipsis" aria-hidden="true">
          …
        </span>
        <UiButton
          v-else
          :variant="page_item === page ? 'primary' : 'secondary'"
          type="button"
          :disabled="page_item === page"
          :aria-current="page_item === page ? 'page' : undefined"
          :aria-label="`Страница ${page_item}`"
          @click="emit('page', page_item)"
        >
          {{ page_item }}
        </UiButton>
      </template>
    </nav>
    <span v-else class="ui-pager__label">Страница {{ page }} / {{ totalPages }}</span>
    <UiButton
      variant="secondary"
      type="button"
      :disabled="page >= totalPages"
      @click="emit('next')"
    >
      Вперёд
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import UiButton from '@/shared/ui/UiButton.vue'

const props = withDefaults(
  defineProps<{
    page: number
    totalPages: number
    showPages?: boolean
  }>(),
  { showPages: false },
)

const pageItems = computed<Array<number | 'ellipsis'>>(() => {
  const total_pages = Math.max(1, props.totalPages)
  const current_page = Math.min(Math.max(1, props.page), total_pages)

  if (total_pages <= 7) {
    return Array.from({ length: total_pages }, (_, index) => index + 1)
  }

  const items: Array<number | 'ellipsis'> = [1]
  const start_page = Math.max(2, current_page - 1)
  const end_page = Math.min(total_pages - 1, current_page + 1)

  if (start_page > 2) items.push('ellipsis')
  for (let page_number = start_page; page_number <= end_page; page_number += 1) {
    items.push(page_number)
  }
  if (end_page < total_pages - 1) items.push('ellipsis')
  items.push(total_pages)

  return items
})

const emit = defineEmits<{
  (e: 'prev'): void
  (e: 'next'): void
  (e: 'page', page: number): void
}>()
</script>

<style scoped>
.ui-pager {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  margin-top: 12px;
  gap: 8px;
}

.ui-pager__pages {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* На телефоне «Назад», семь номеров и «Вперёд» в одну строку не помещаются (375 px):
   номера уходят отдельной строкой над кнопками. */
@media (max-width: 600px) {
  .ui-pager__pages {
    order: -1;
    flex-basis: 100%;
  }
}

.ui-pager__ellipsis {
  color: #586578;
  padding: 0 2px;
}

.ui-pager__label {
  color: #586578;
  font-size: 12px;
}
</style>
